import React, { useState, useEffect, useMemo } from 'react'
import { Provider, useTable } from 'tinybase/ui-react'
import { SYNC_STATUS } from '../utils/subject_store'

const SyncStatusBadgesContent = ({ projectIds, subjectStore }) => {
  const [loading, setLoading] = useState(true)

  // Use TinyBase hooks to trigger updates when tables change
  const subjectsTable = useTable('subjects')
  const photosTable = useTable('photos')
  const audioTable = useTable('audio')

  // Calculate sync stats from tables
  const syncStats = useMemo(() => {
    try {
      if (!subjectsTable || !photosTable || !audioTable) {
        return { queueCount: 0, storageUsage: 0 }
      }

      let allSubjects = Object.values(subjectsTable)

      // Filter by project IDs if provided
      if (projectIds && projectIds.length > 0) {
        allSubjects = allSubjects.filter(subject =>
          projectIds.includes(subject.projectId)
        )
      }

      // Count queued subjects (including those without sync status set yet)
      const queuedSubjects = allSubjects.filter(subject => {
        return !subject.syncStatus ||
               subject.syncStatus === SYNC_STATUS.PENDING ||
               subject.syncStatus === SYNC_STATUS.IN_PROGRESS
      })

      // Calculate storage usage for unsynced content
      let totalSize = 0

      Object.values(photosTable).forEach(photo => {
        const subject = subjectsTable[photo.subjectId]
        if (subject && subject.syncStatus !== SYNC_STATUS.COMPLETED) {
          totalSize += photo.size || 0
        }
      })

      Object.values(audioTable).forEach(audio => {
        const subject = subjectsTable[audio.subjectId]
        if (subject && subject.syncStatus !== SYNC_STATUS.COMPLETED) {
          totalSize += audio.size || 0
        }
      })

      return {
        queueCount: queuedSubjects.length,
        storageUsage: totalSize
      }
    } catch (error) {
      console.error('Error calculating sync stats:', error)
      return { queueCount: 0, storageUsage: 0 }
    }
  }, [subjectsTable, photosTable, audioTable, projectIds])

  // Wait for store to be ready on mount
  useEffect(() => {
    const initializeStore = async () => {
      try {
        setLoading(true)
        await subjectStore.waitForReady()
      } catch (error) {
        console.error('Error initializing store:', error)
      } finally {
        setLoading(false)
      }
    }

    initializeStore()
  }, [subjectStore])

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B'

    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  if (loading) {
    return (
      <div className="mt-auto flex flex-col gap-3">
        <div className="bg-blue-100 rounded-lg bg-base-100 shadow-sm flex justify-center items-center p-3">
          <span className="loading loading-spinner loading-sm"></span>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-auto flex flex-col gap-3" data-turbo-permanent>
      {/* Queue count indicator */}
      <a href="/projects" className="block">
        <div className="bg-blue-100 rounded-lg bg-base-100 shadow-sm flex justify-between items-center p-3 hover:shadow-md">
          <div className="flex flex-col gap-1">
            <div className="flex items-center text-sm gap-2 font-semibold">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Queue Count:</span>
            </div>
            <div className="badge badge-warning text-base-content">
              <span>{syncStats.queueCount}</span>
              <span>subjects pending</span>
            </div>
          </div>
          <div className="flex flex-col">
            <svg className="w-5 h-5 text-base-content" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </a>

      {/* Storage usage indicator */}
      <div className="rounded-lg shadow-sm bg-warning/30">
        <div className="flex items-center p-3 text-sm gap-1 font-semibold">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
          <span>Queued Storage:</span>
          <span className="font-semibold">{formatBytes(syncStats.storageUsage)}</span>
        </div>
      </div>
    </div>
  )
}

// Wrapper component with TinyBase Provider
const SyncStatusBadges = (props) => {
  const { subjectStore } = props
  return (
    <Provider store={subjectStore.getStore()}>
      <SyncStatusBadgesContent {...props} />
    </Provider>
  )
}

export default SyncStatusBadges